import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Languages, Search } from 'lucide-react';
import { fmt } from '@/pages/Dashboard';
import { Input } from '@/components/ui/input';
import type { PageviewPathData } from '@/types/pageview-report';
import { languageFlagEmoji, languageLabel } from './shared';
import { EmptyState, SortableTh } from './DomainsTab';

type SortKey = keyof Pick<
    PageviewPathData,
    'pageviews' | 'unique_sessions' | 'revenue' | 'impressions' | 'rps' | 'rpm' | 'ipvu'
>;

interface Props {
    data: PageviewPathData[];
}

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
    { key: 'pageviews', label: 'PV' },
    { key: 'unique_sessions', label: 'Sessões' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'impressions', label: 'Impr.' },
    { key: 'rps', label: 'RPS' },
    { key: 'rpm', label: 'RPM' },
    { key: 'ipvu', label: 'IPVU' },
];

export function PathsTab({ data }: Props) {
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('pageviews');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const filtered = useMemo(() => {
        let arr = data;
        if (search.trim()) {
            const s = search.toLowerCase();
            arr = arr.filter((p) => p.path.toLowerCase().includes(s));
        }
        return [...arr].sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            return sortDir === 'desc' ? bv - av : av - bv;
        });
    }, [data, search, sortKey, sortDir]);

    const toggle = (path: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    if (data.length === 0) {
        return <EmptyState message="Sem dados de paths no período selecionado" />;
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <Languages className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">
                        Paths por linguagem
                    </h3>
                    <span className="text-xs text-gray-500">({filtered.length})</span>
                </div>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filtrar path..."
                        className="h-8 text-sm w-64 pl-8"
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="w-8" />
                            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Path base
                            </th>
                            {COLUMNS.map((c) => (
                                <SortableTh
                                    key={c.key}
                                    label={c.label}
                                    hint={c.hint}
                                    active={sortKey === c.key}
                                    dir={sortDir}
                                    onClick={() => toggleSort(c.key)}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map((p) => {
                            const isOpen = expanded.has(p.path);
                            return (
                                <>
                                    <tr
                                        key={p.path}
                                        onClick={() => toggle(p.path)}
                                        className="hover:bg-gray-50 cursor-pointer"
                                    >
                                        <td className="pl-3 text-gray-400">
                                            {isOpen ? (
                                                <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4" />
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-gray-900 truncate max-w-[400px]">
                                                    {p.path}
                                                </span>
                                                <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                                    {p.languages.length} idioma{p.languages.length === 1 ? '' : 's'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                            {fmt.number(p.pageviews)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                            {fmt.number(p.unique_sessions)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-emerald-700 font-medium">
                                            {fmt.currency(p.revenue)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                            {fmt.number(p.impressions)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {fmt.currency(p.rps)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {fmt.currency(p.rpm)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {p.ipvu.toFixed(2)}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr key={`${p.path}-langs`}>
                                            <td colSpan={9} className="p-0">
                                                <div className="bg-gray-50/60 border-y border-gray-200 px-6 py-3">
                                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                                        Breakdown por idioma
                                                    </div>
                                                    {p.languages.length === 0 ? (
                                                        <div className="text-xs text-gray-500 py-3">
                                                            Sem breakdown de idioma
                                                        </div>
                                                    ) : (
                                                        <div className="rounded border border-gray-200 bg-white overflow-hidden">
                                                            <table className="min-w-full text-xs">
                                                                <thead className="bg-white border-b border-gray-200">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Idioma
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            PV
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Sess
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Revenue
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            RPS
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            RPM
                                                                        </th>
                                                                        <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            IPVU
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100">
                                                                    {p.languages.map((lg, idx) => (
                                                                        <tr key={idx} className="hover:bg-gray-50">
                                                                            <td className="px-3 py-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-base leading-none">
                                                                                        {languageFlagEmoji(lg.language)}
                                                                                    </span>
                                                                                    <span className="font-medium text-gray-900">
                                                                                        {languageLabel(lg.language)}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                                                                                {fmt.number(lg.pageviews)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                                                                                {fmt.number(lg.unique_sessions)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">
                                                                                {fmt.currency(lg.revenue)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-900 font-medium">
                                                                                {fmt.currency(lg.rps)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-900 font-medium">
                                                                                {fmt.currency(lg.rpm)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-900 font-medium">
                                                                                {lg.ipvu.toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
